/**
 * Magnetic Ingredients Section
 * Interactive ingredient discovery with compatibility physics
 */

class MagneticIngredients {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.debug = false;

    // DOM Elements
    this.grid = section.querySelector('[data-grid]');
    this.center = section.querySelector('[data-center]');
    this.centerText = this.center?.querySelector('.magnetic-ingredients__center-text');
    this.particlesContainer = section.querySelector('[data-particles]');
    this.productCard = section.querySelector('[data-product-card]');
    this.productInner = section.querySelector('[data-product-inner]');
    this.clearBtn = section.querySelector('[data-clear]');
    this.selectedItemsContainer = section.querySelector('[data-selected-items]');
    this.noteContainer = section.querySelector('[data-note]');
    this.orbs = section.querySelectorAll('[data-orb]');

    // Data
    this.productMatches = this.loadData('[data-product-matches]');
    this.compatibilityNotes = this.loadData('[data-compatibility-notes]');

    // State
    this.selectedIngredients = [];
    this.repellingOrb = null;
    this.initialCenterText = this.centerText?.textContent || 'Select 2 ingredients';

    this.init();
  }

  log(...args) {
    if (this.debug) console.log(...args);
  }

  loadData(selector) {
    const script = this.section.querySelector(selector);
    if (!script) {
      if (this.debug) console.warn('Data script not found:', selector);
      return [];
    }
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      if (this.debug) console.error('Failed to parse data:', selector, e);
      return [];
    }
  }

  init() {
    this.bindEvents();
    this.updateOrbPositions();
  }

  bindEvents() {
    // Orb clicks
    this.orbs.forEach(orb => {
      orb.addEventListener('click', () => this.handleOrbClick(orb));
    });

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearSelection());
    }

    // Add to cart from product card + close button (delegated)
    this.section.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        this.handleAddToCart(e.target);
      }
      if (e.target.matches('[data-close-product]')) {
        this.clearSelection();
      }
    });

    // Keyboard: Escape to dismiss product card
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.productCard?.classList.contains('is-visible')) {
        this.clearSelection();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => this.updateOrbPositions());

    // Swipe-to-dismiss on mobile
    this.initSwipeToDismiss();
  }

  initSwipeToDismiss() {
    let startY = 0;
    let currentY = 0;
    const inner = this.productInner;
    if (!inner) return;

    inner.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      currentY = startY;
    }, { passive: true });

    inner.addEventListener('touchmove', (e) => {
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;
      if (diff > 0) {
        inner.style.transform = `translateY(${diff}px)`;
        inner.style.opacity = Math.max(0, 1 - diff / 200);
      }
    }, { passive: true });

    inner.addEventListener('touchend', () => {
      const diff = currentY - startY;
      if (diff > 80) {
        this.clearSelection();
      }
      inner.style.transform = '';
      inner.style.opacity = '';
      startY = 0;
      currentY = 0;
    });
  }

  updateOrbPositions() {
    this.orbs.forEach(orb => {
      const rect = orb.getBoundingClientRect();
      const gridRect = this.grid.getBoundingClientRect();
      orb.dataset.originalLeft = ((rect.left - gridRect.left + rect.width / 2) / gridRect.width * 100).toFixed(2);
      orb.dataset.originalTop = ((rect.top - gridRect.top + rect.height / 2) / gridRect.height * 100).toFixed(2);
    });
  }

  handleOrbClick(orb) {
    const ingredientId = orb.dataset.ingredientId;
    const ingredientName = orb.dataset.ingredientName;

    this.log('Orb clicked:', ingredientName);

    // If already selected, deselect
    if (this.selectedIngredients.some(i => i.id === ingredientId)) {
      this.deselectIngredient(ingredientId);
      return;
    }

    // Check compatibility with last selected
    if (this.selectedIngredients.length > 0) {
      const lastSelected = this.selectedIngredients[this.selectedIngredients.length - 1];
      const lastOrb = this.section.querySelector(`[data-ingredient-id="${lastSelected.id}"]`);

      if (this.isIncompatible(lastOrb, orb)) {
        this.triggerRepel(orb);
        this.showIncompatibilityNote(lastSelected.name, ingredientName);
        return;
      }
    }

    // Select the ingredient
    this.selectIngredient(orb, ingredientId, ingredientName);
  }

  isIncompatible(orbA, orbB) {
    const incompatibleA = (orbA.dataset.incompatible || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
    const incompatibleB = (orbB.dataset.incompatible || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
    const nameA = orbA.dataset.ingredientName.toLowerCase();
    const nameB = orbB.dataset.ingredientName.toLowerCase();

    return incompatibleA.includes(nameB) || incompatibleB.includes(nameA);
  }

  selectIngredient(orb, id, name) {
    const color = getComputedStyle(orb).getPropertyValue('--orb-color').trim();
    const emoji = orb.querySelector('.magnetic-ingredients__orb-icon')?.textContent || '•';

    this.selectedIngredients.push({ id, name, color, emoji });

    this.log('Selected ingredients:', this.selectedIngredients.map(i => i.name));

    // Keep only last 2
    if (this.selectedIngredients.length > 2) {
      const removed = this.selectedIngredients.shift();
      const removedOrb = this.section.querySelector(`[data-ingredient-id="${removed.id}"]`);
      if (removedOrb) {
        removedOrb.classList.remove('is-selected', 'is-merging', 'is-hidden');
        removedOrb.setAttribute('aria-pressed', 'false');
        removedOrb.removeAttribute('title');
        this.resetOrbPosition(removedOrb);
      }
    }

    // Update orb state
    orb.classList.add('is-selected');
    orb.setAttribute('aria-pressed', 'true');
    orb.setAttribute('title', `Click to deselect ${name}`);

    // Animate toward center
    this.animateOrbToCenter(orb);

    // Update center text to show progress
    this.updateCenterText();

    // Update UI
    this.updateSelectedUI();
    this.updateDisabledOrbs();
    this.updateClearButton();
    this.center.classList.add('is-active');

    // Check for product match when 2 selected
    if (this.selectedIngredients.length === 2) {
      this.triggerMergeAnimation();
    } else {
      this.hideProductCard();
    }
  }

  updateCenterText() {
    if (!this.centerText) return;

    if (this.selectedIngredients.length === 0) {
      this.centerText.textContent = this.initialCenterText;
    } else if (this.selectedIngredients.length === 1) {
      this.centerText.textContent = '1 of 2 selected';
    } else {
      this.centerText.textContent = '';
    }
  }

  triggerMergeAnimation() {
    const selectedOrbs = this.selectedIngredients.map(ing =>
      this.section.querySelector(`[data-ingredient-id="${ing.id}"]`)
    ).filter(Boolean);

    this.center.classList.add('is-merging');

    selectedOrbs.forEach(orb => {
      orb.classList.add('is-merging');
    });

    setTimeout(() => {
      this.createParticles();

      selectedOrbs.forEach(orb => {
        orb.classList.add('is-hidden');
        orb.classList.remove('is-merging');
      });

      setTimeout(() => {
        this.center.classList.remove('is-merging');
      }, 300);

      setTimeout(() => {
        this.checkForProductMatch();
      }, 200);
    }, 600);
  }

  deselectIngredient(id) {
    this.selectedIngredients = this.selectedIngredients.filter(i => i.id !== id);

    const orb = this.section.querySelector(`[data-ingredient-id="${id}"]`);
    if (orb) {
      orb.classList.remove('is-selected', 'is-merging', 'is-hidden');
      orb.setAttribute('aria-pressed', 'false');
      orb.removeAttribute('title');
      this.resetOrbPosition(orb);
    }

    // Also reset any other selected orbs that might be hidden
    this.selectedIngredients.forEach(ing => {
      const selectedOrb = this.section.querySelector(`[data-ingredient-id="${ing.id}"]`);
      if (selectedOrb) {
        selectedOrb.classList.remove('is-hidden', 'is-merging');
        this.animateOrbToCenter(selectedOrb);
      }
    });

    this.updateCenterText();
    this.updateSelectedUI();
    this.updateDisabledOrbs();
    this.updateClearButton();
    this.hideProductCard();
    this.hideNote();

    if (this.selectedIngredients.length === 0) {
      this.center.classList.remove('is-active', 'has-match', 'is-merging');
    }
  }

  animateOrbToCenter(orb) {
    const orbRect = orb.getBoundingClientRect();
    const centerRect = this.center.getBoundingClientRect();

    const orbCenterX = orbRect.left + orbRect.width / 2;
    const orbCenterY = orbRect.top + orbRect.height / 2;
    const targetX = centerRect.left + centerRect.width / 2;
    const targetY = centerRect.top + centerRect.height / 2;

    const selectedIndex = this.selectedIngredients.length - 1;
    const offsetX = selectedIndex === 0 ? -35 : 35;

    const translateX = targetX - orbCenterX + offsetX;
    const translateY = targetY - orbCenterY;

    orb.dataset.originalTransform = orb.style.transform || '';
    orb.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.6)`;
  }

  resetOrbPosition(orb) {
    orb.style.transform = orb.dataset.originalTransform || '';
  }

  triggerRepel(orb) {
    orb.classList.add('is-repelling');
    this.repellingOrb = orb;

    setTimeout(() => {
      orb.classList.remove('is-repelling');
      this.repellingOrb = null;
    }, 600);
  }

  showIncompatibilityNote(ingredient1, ingredient2) {
    const note = this.compatibilityNotes.find(n => {
      const ingredients = n.ingredients.map(i => i.toLowerCase().trim());
      return (
        ingredients.includes(ingredient1.toLowerCase()) && ingredients.includes(ingredient2.toLowerCase())
      );
    });

    if (note && this.noteContainer) {
      this.noteContainer.innerHTML = note.note;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible';
      this.noteContainer.classList.add(note.isWarning ? 'is-warning' : 'is-success');
    } else if (this.noteContainer) {
      this.noteContainer.innerHTML = `⚠️ ${ingredient1} and ${ingredient2} shouldn't be used together. They may cause irritation or reduce effectiveness.`;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible is-warning';
    }

    // Suggest compatible alternatives
    this.appendCompatibleSuggestion(ingredient2);

    // Auto-hide after a delay
    setTimeout(() => {
      if (this.selectedIngredients.length < 2) {
        this.hideNote();
      }
    }, 5000);
  }

  appendCompatibleSuggestion(rejectedName) {
    if (!this.noteContainer || this.selectedIngredients.length === 0) return;

    const lastSelected = this.selectedIngredients[this.selectedIngredients.length - 1];
    const lastOrb = this.section.querySelector(`[data-ingredient-id="${lastSelected.id}"]`);
    const suggestions = [];

    this.orbs.forEach(orb => {
      const isSelected = this.selectedIngredients.some(i => i.id === orb.dataset.ingredientId);
      const isRejected = orb.dataset.ingredientName === rejectedName;
      if (!isSelected && !isRejected && !this.isIncompatible(lastOrb, orb)) {
        suggestions.push(orb.dataset.ingredientName);
      }
    });

    if (suggestions.length > 0) {
      const suggestionText = suggestions.slice(0, 2).join(' or ');
      this.noteContainer.innerHTML += `<br><small>Try pairing with <strong>${suggestionText}</strong> instead.</small>`;
    }
  }

  hideNote() {
    if (this.noteContainer) {
      this.noteContainer.classList.remove('is-visible');
    }
  }

  updateSelectedUI() {
    if (!this.selectedItemsContainer) return;

    if (this.selectedIngredients.length === 0) {
      this.selectedItemsContainer.innerHTML = `
        <span class="magnetic-ingredients__selected-placeholder">
          ${this.section.querySelector('.magnetic-ingredients__selected-placeholder')?.textContent || 'Click ingredients to select...'}
        </span>
      `;
    } else {
      this.selectedItemsContainer.innerHTML = this.selectedIngredients.map(ing => `
        <div class="magnetic-ingredients__selected-tag" style="--tag-color: ${ing.color}">
          <span class="magnetic-ingredients__selected-tag-icon">${ing.emoji}</span>
          ${ing.name}
        </div>
      `).join('');
    }
  }

  updateDisabledOrbs() {
    if (this.selectedIngredients.length === 0) {
      this.orbs.forEach(orb => {
        orb.classList.remove('is-disabled', 'is-faded', 'is-compatible');
      });
      return;
    }

    const lastSelected = this.selectedIngredients[this.selectedIngredients.length - 1];
    const lastOrb = this.section.querySelector(`[data-ingredient-id="${lastSelected.id}"]`);

    this.orbs.forEach(orb => {
      const isSelected = this.selectedIngredients.some(i => i.id === orb.dataset.ingredientId);

      if (isSelected) {
        orb.classList.remove('is-disabled', 'is-faded', 'is-compatible');
        return;
      }

      if (this.isIncompatible(lastOrb, orb)) {
        orb.classList.add('is-faded');
        orb.classList.remove('is-disabled', 'is-compatible');
      } else {
        orb.classList.add('is-compatible');
        orb.classList.remove('is-disabled', 'is-faded');
      }
    });
  }

  updateClearButton() {
    if (!this.clearBtn) return;

    if (this.selectedIngredients.length > 0) {
      this.clearBtn.classList.add('is-visible');
    } else {
      this.clearBtn.classList.remove('is-visible');
    }
  }

  clearSelection() {
    this.orbs.forEach(orb => {
      orb.classList.remove('is-selected', 'is-disabled', 'is-faded', 'is-merging', 'is-hidden', 'is-compatible');
      orb.setAttribute('aria-pressed', 'false');
      orb.removeAttribute('title');
      this.resetOrbPosition(orb);
    });

    this.selectedIngredients = [];

    this.updateCenterText();
    this.updateSelectedUI();
    this.updateClearButton();
    this.hideProductCard();
    this.hideNote();
    this.center.classList.remove('is-active', 'has-match', 'is-merging');

    // Return focus to first orb for keyboard users
    const firstOrb = this.section.querySelector('[data-orb]');
    if (firstOrb) {
      firstOrb.focus();
    }
  }

  createParticles() {
    if (!this.particlesContainer) return;

    const particleCount = 16;
    this.particlesContainer.innerHTML = '';

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'magnetic-ingredients__particle';

      const angle = (i * (360 / particleCount)) * (Math.PI / 180);
      const distance = 100 + Math.random() * 60;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;

      particle.style.setProperty('--tx', `${tx}px`);
      particle.style.setProperty('--ty', `${ty}px`);
      particle.style.animationDelay = `${Math.random() * 0.1}s`;

      this.particlesContainer.appendChild(particle);
    }

    setTimeout(() => {
      this.particlesContainer.innerHTML = '';
    }, 1000);
  }

  normalizeIngredientName(name) {
    return name.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  checkForProductMatch() {
    const selectedNames = this.selectedIngredients.map(i => this.normalizeIngredientName(i.name));

    this.log('Checking for product match...', selectedNames);

    const match = this.productMatches.find(pm => {
      if (!pm.ingredients || !Array.isArray(pm.ingredients)) return false;

      const matchIngredients = pm.ingredients.map(i => this.normalizeIngredientName(i));

      const isMatch = (
        matchIngredients.length === selectedNames.length &&
        matchIngredients.every(mi => selectedNames.includes(mi)) &&
        selectedNames.every(si => matchIngredients.includes(si))
      );

      if (isMatch) this.log('Match found!', pm);
      return isMatch;
    });

    if (match) {
      this.showProductCard(match);
      this.showMatchNote(match);
      this.center.classList.add('has-match');
    } else {
      this.log('No exact match found, showing generic');
      this.showGenericMatch();
    }
  }

  showProductCard(match) {
    if (!this.productInner || !this.productCard) return;

    const product = match.product;
    if (!product) return;

    const hasImage = product.image && product.image !== 'null' && product.image !== null;
    const addToCartText = this.section.dataset.addToCartText || 'Add to Cart';
    const isSoldOut = product.available === false;
    const hasVariant = !!product.variantId;
    const hasProductUrl = product.url && product.url !== '#';

    this.log('Showing product card:', product);

    let buttonHtml;
    if (isSoldOut) {
      buttonHtml = `
        <button class="magnetic-ingredients__product-btn" disabled>Sold Out</button>
        ${hasProductUrl
          ? `<a href="${product.url}" class="magnetic-ingredients__product-link">View product for restock notifications →</a>`
          : ''
        }
      `;
    } else {
      buttonHtml = `
        <button
          class="magnetic-ingredients__product-btn"
          data-add-to-cart
          data-variant-id="${product.variantId || ''}"
          data-product-url="${product.url || '#'}"
          ${!hasVariant ? 'disabled' : ''}
        >
          ${addToCartText}
          <svg class="magnetic-ingredients__btn-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 64 64" fill="currentColor">
            <path d="M12,56.43,36.48,32,12,7.52,19.53,0,51.61,32,19.53,64Z"></path>
          </svg>
        </button>
      `;
    }

    this.productInner.innerHTML = `
      <button class="magnetic-ingredients__product-close" data-close-product aria-label="Close product card">✕</button>
      <div class="magnetic-ingredients__product-image-wrap">
        ${hasImage
          ? `<img src="${product.image}" alt="${product.title}" class="magnetic-ingredients__product-image" loading="lazy">`
          : `<span class="magnetic-ingredients__product-emoji">🧴</span>`
        }
      </div>
      <h4 class="magnetic-ingredients__product-title">${product.title || 'Recommended Product'}</h4>
      <p class="magnetic-ingredients__product-benefit">${match.benefit || ''}</p>
      <p class="magnetic-ingredients__product-price">${product.price || ''}</p>
      ${buttonHtml}
      ${hasProductUrl
        ? `<a href="${product.url}" class="magnetic-ingredients__product-view-link">View full product details</a>`
        : ''
      }
    `;

    this.productCard.classList.add('is-visible');

    // Focus management: move focus to close button after animation
    const closeBtn = this.productInner.querySelector('[data-close-product]');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 550);
    }
  }

  showGenericMatch() {
    if (this.noteContainer) {
      const names = this.selectedIngredients.map(i => i.name).join(' + ');
      this.noteContainer.innerHTML = `💡 ${names}: An interesting combination! These can work well together. Try selecting different ingredients to discover our curated product matches.`;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible is-success';
    }
    this.center.classList.add('has-match');

    // Show the orbs again so the user can try a different combo
    this.selectedIngredients.forEach(ing => {
      const orb = this.section.querySelector(`[data-ingredient-id="${ing.id}"]`);
      if (orb) {
        orb.classList.remove('is-hidden');
      }
    });
  }

  showMatchNote(match) {
    if (!this.noteContainer) return;

    if (match.note) {
      this.noteContainer.innerHTML = match.note;
      this.noteContainer.className = 'magnetic-ingredients__note is-visible';
      this.noteContainer.classList.add(match.isWarning ? 'is-warning' : 'is-success');
    }
  }

  hideProductCard() {
    if (this.productCard) {
      this.productCard.classList.remove('is-visible');
    }
    this.center.classList.remove('has-match');
  }

  async handleAddToCart(button) {
    const variantId = button.dataset.variantId;
    const productUrl = button.dataset.productUrl;

    if (!variantId) {
      if (productUrl && productUrl !== '#') {
        window.location.href = productUrl;
      }
      return;
    }

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `
      <span class="magnetic-ingredients__btn-loading"></span>
      Adding...
    `;

    try {
      const cartDrawer = document.querySelector('hdt-cart-drawer');
      const sectionId = cartDrawer?.getAttribute('section-id') || 'cart-drawer';

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          sections: sectionId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();

      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        Added!
      `;

      this.openCartDrawer(result);

      setTimeout(() => {
        button.disabled = false;
        button.innerHTML = originalText;
      }, 2000);

    } catch (error) {
      console.error('Add to cart error:', error);

      button.innerHTML = `Error - Try Again`;

      setTimeout(() => {
        button.disabled = false;
        button.innerHTML = originalText;
      }, 2000);
    }
  }

  openCartDrawer(cartData) {
    const cartDialog = document.querySelector('#CartDrawer');

    // Dispatch events for the theme
    document.dispatchEvent(new CustomEvent('cart:drawer:change', {
      bubbles: true,
      detail: { cart: cartData }
    }));
    document.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: { cart: cartData }
    }));

    // Try to open the drawer
    let drawerOpened = false;
    if (cartDialog) {
      try {
        if (typeof cartDialog.showModal === 'function' && !cartDialog.open) {
          cartDialog.showModal();
          drawerOpened = true;
        } else if (cartDialog.open) {
          drawerOpened = true;
        } else {
          cartDialog.setAttribute('open', '');
          drawerOpened = true;
        }
      } catch (e) {
        console.warn('Could not open cart drawer:', e);
      }
    }

    // Show fallback if drawer didn't open
    if (!drawerOpened) {
      this.showCartFallbackNotification();
    }

    this.updateCartCount();
  }

  showCartFallbackNotification() {
    const btn = this.productInner?.querySelector('[data-add-to-cart]');
    if (!btn) return;

    // Remove any existing notice
    const existing = this.productInner.querySelector('.magnetic-ingredients__cart-notice');
    if (existing) existing.remove();

    const notice = document.createElement('p');
    notice.className = 'magnetic-ingredients__cart-notice';
    notice.textContent = 'Item added! View your cart to check out.';
    notice.setAttribute('role', 'status');
    btn.insertAdjacentElement('afterend', notice);

    setTimeout(() => notice.remove(), 4000);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      const cart = await response.json();

      const cartDrawer = document.querySelector('hdt-cart-drawer');
      if (cartDrawer) {
        cartDrawer.setAttribute('data-count', cart.item_count);
      }

      const cartCountSelectors = [
        '.cart-count',
        '.cart-count-bubble',
        '[data-cart-count]',
        '.header__cart-count',
        '.cart-notification-count',
        '#cart-icon-bubble span',
        '[ref="hdt-cart"] [data-count]'
      ];

      cartCountSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el.hasAttribute('data-count')) {
            el.setAttribute('data-count', cart.item_count);
          } else {
            el.textContent = cart.item_count;
          }
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      });

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));

    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }
}

// Initialize all sections
function initMagneticIngredients() {
  document.querySelectorAll('.magnetic-ingredients').forEach(section => {
    if (!section.dataset.initialized) {
      new MagneticIngredients(section);
      section.dataset.initialized = 'true';
    }
  });
}

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMagneticIngredients);
} else {
  initMagneticIngredients();
}

// Re-init on Shopify section events (for theme editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('.magnetic-ingredients')) {
    initMagneticIngredients();
  }
});

document.addEventListener('shopify:section:reorder', initMagneticIngredients);
